import { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTimes } from 'react-icons/fa';

const initialFormData = {
    table_name: '',
    template_name: '',
    header_image: null,
    header_type: '',
    is_header_static: 0,
    is_header_required: 1, // 0 = Yes, 1 = No
    is_header_id: 1,       // 0 = No, 1 = Yes
    header_id_val: '',
    hcolumn: '',
    wid: '',
    user_id: '',
    pid: '',
    lang: '',
    selectedColumns: [],
};

const DEFAULT_COLUMNS = ['id', 'mobile', 'group_id', 'subgroup_id', 'member_id', 'role', 'flag', 'user_id'];

export default function EbotConfigManager() {
    const [formData, setFormData] = useState(initialFormData);
    const [configs, setConfigs] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [columns, setColumns] = useState([]);
    const [languagePosts, setLanguagePosts] = useState([]);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [tableOptions, setTableOptions] = useState([]);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetch Columns based on Table Name
    useEffect(() => {
        if (formData.table_name) {
            fetchColumns(formData.table_name);
        } else {
            setColumns([]);
            setFormData(prev => ({ ...prev, selectedColumns: [] }));
        }
    }, [formData.table_name]);

    const fetchColumns = async (tableName) => {
        try {
            const response = await axios.get(`https://botapi.emedha.in/pages/api/api/tables/${tableName}/columns`);
            setColumns(response.data);
        } catch (error) {
            console.error("Failed to fetch columns:", error);
            setColumns([]);
        }
    };

    const handleColumnCheckbox = (e) => {
        const colName = e.target.value;
        const checked = e.target.checked;
        setFormData(prev => {
            let newSelectedColumns = [...(prev.selectedColumns || [])];
            if (checked) {
                if (!newSelectedColumns.includes(colName)) {
                    newSelectedColumns.push(colName);
                }
            } else {
                newSelectedColumns = newSelectedColumns.filter(c => c !== colName);
            }
            return { ...prev, selectedColumns: newSelectedColumns };
        });
    };

    const visibleColumns = columns.filter(col => !DEFAULT_COLUMNS.includes(col));

    // Session Management & Config Initialization
    useEffect(() => {
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setFormData((prev) => ({
                    ...prev,
                    wid: parsedUser.wa_id || '',
                    user_id: parsedUser.id || '',
                    pid: parsedUser.p_id || '',
                }));
            } catch (e) {
                console.error('Error parsing user from sessionStorage:', e);
            }
        }
        fetchConfigs();
    }, []);

    // Fetch Languages when criteria met
    useEffect(() => {
        if (formData.user_id && formData.wid) {
            fetchLanguagePosts(formData.user_id, formData.wid);
        }
    }, [formData.user_id, formData.wid]);

    const fetchLanguagePosts = async (user_id, wa_id) => {
        try {
            const response = await axios.get('https://botapi.emedha.in/pages/api/api/language-get-all', {
                params: { user_id, wa_id }
            });
            setLanguagePosts(response.data);
        } catch (error) {
            console.error('Failed to fetch language posts:', error);
        }
    };

    // Fetch Templates
    useEffect(() => {
        const fetchTemplates = async () => {
            const storedUser = sessionStorage.getItem('user');
            if (!storedUser) return;
            try {
                const user = JSON.parse(storedUser);
                const response = await fetch(`https://emedha.com/echatbot/enotifier/getTemplateStatus.php?limit=10&wid=${user.wa_id}`);
                const json = await response.json();
                const approvedTemplates = json.data?.filter(item => item.status === "APPROVED") || [];
                setTemplateOptions(approvedTemplates);
            } catch (error) {
                console.error("Failed to fetch templates:", error);
            }
        };
        fetchTemplates();
    }, []);

    // Fetch Table dropdown options
    useEffect(() => {
        if (formData.user_id) {
            fetchTableOptions(formData.user_id);
        }
    }, [formData.user_id]);

    const fetchTableOptions = async (userId) => {
        try {
            const response = await axios.get('https://botapi.emedha.in/pages/api/api/tables', {
                params: { userId }
            });
            setTableOptions(response.data);
        } catch (err) {
            console.error("Failed to fetch table names:", err);
        }
    };

    const fetchConfigs = async () => {
        try {
            const user = JSON.parse(sessionStorage.getItem('user'));
            if (!user) return;
            const adminId = user.role === "admin" ? user.id : user.userId;

            if (!adminId) {
                alert("User ID missing!");
                return;
            }

            const res = await axios.get(`https://botapi.emedha.in/pages/api/ebot-configs/${adminId}`);
            const groupsData = res.data;

            let filteredGroups = groupsData;
            if (user.role === "member") {
                filteredGroups = groupsData.filter(group => group.role === "member" && group.member_id === user.id);
            }
            setConfigs(filteredGroups);
            setCurrentPage(1);
        } catch (err) {
            console.error(err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        const newValue = type === 'radio' ? parseInt(value, 10) : value;
        setFormData((prev) => ({ ...prev, [name]: newValue }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const type = formData.header_type;
        const fileName = file.name.toLowerCase();
        const isValid =
            (type === 'image' && /\.(jpg|jpeg|png)$/.test(fileName)) ||
            (type === 'pdf' && /\.pdf$/.test(fileName)) ||
            (type === 'video' && /\.(mp4|mov|avi|mkv)$/.test(fileName));

        if (!isValid) {
            alert(`Invalid file type for selected header type: ${type.toUpperCase()}`);
            e.target.value = '';
            return;
        }
        setFormData((prev) => ({ ...prev, header_image: file }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const storedUser = sessionStorage.getItem('user');
            const parsedUser = storedUser ? JSON.parse(storedUser) : {};

            const isHeaderRequired = formData.is_header_required === 0;
            const isHeaderId = formData.is_header_id === 1;
            const isDynamic = formData.is_header_static === 1;

            const finalPayload = {
                ...formData,
                wid: parsedUser.wa_id || formData.wid,
                user_id: parsedUser.id || formData.user_id,
                pid: parsedUser.p_id || formData.pid,
                tcolumn: formData.selectedColumns?.join(',') || '',
                hcolumn: (isHeaderRequired && !isHeaderId && isDynamic) ? formData.hcolumn : '',
                header_id_val: (isHeaderRequired && isHeaderId) ? formData.header_id_val : '',
                header_type: isHeaderRequired ? formData.header_type : '',
            };

            const data = new FormData();
            for (let key in finalPayload) {
                if (key === 'selectedColumns') continue;

                if (key === 'header_image' && isHeaderRequired && isHeaderId) {
                    data.append('header_image', formData.header_id_val);
                    continue;
                }

                if (finalPayload[key] !== null && finalPayload[key] !== undefined) {
                    data.append(key, finalPayload[key]);
                }
            }

            if (editingId) {
                await axios.put(`https://botapi.emedha.in/pages/api/ebot_update/${editingId}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                alert("Template updated successfully!");
            } else {
                alert("Editing ID not found. Update cannot proceed.");
                return;
            }

            setFormData(initialFormData);
            setEditingId(null);
            setShowModal(false);
            fetchConfigs();
        } catch (err) {
            console.error(err);
            alert("An error occurred while submitting the template.");
        }
    };

    const handleEdit = (config) => {
        setFormData({
            ...config,
            is_header_static: parseInt(config.is_header_static, 10) || 0,
            is_header_required: config.is_header_required !== undefined ? parseInt(config.is_header_required, 10) : 1,
            is_header_id: config.is_header_id !== undefined ? parseInt(config.is_header_id, 10) : 1,
            header_id_val: config.header_id_val || '',
            header_image: null,
            selectedColumns: config.tcolumn ? config.tcolumn.split(',') : []
        });
        setEditingId(config.id);
        setShowModal(true);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData(initialFormData);
        setShowModal(false);
    };

    // --- Pagination Computations ---
    const totalPages = Math.ceil(configs.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = configs.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div style={{
            fontSize: '13px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            backgroundColor: '#f8fafc',
            minHeight: '100vh',
            color: '#1e293b',
        }}>

            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>Ebot Configurations</h2>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>Modify and maintain configuration modules</p>
            </div>

            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.3)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff', padding: '28px', width: '650px', maxWidth: '95%',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', borderRadius: '12px', maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Modify Configuration</h3>
                            <button onClick={handleCancelEdit} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                                <FaTimes size={16} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Table Name</label>
                                    <select name="table_name" value={formData.table_name} onChange={handleInputChange} required
                                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%', outline: 'none' }}>
                                        <option value="">Select Table Target</option>
                                        {tableOptions.map((table) => (
                                            <option key={table.sno} value={table.table_name}>{table.table_name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Template Name</label>
                                    <select name="template_name" value={formData.template_name} onChange={(e) => {
                                        const selectedName = e.target.value;
                                        const selected = templateOptions.find(t => t.name === selectedName);
                                        setFormData(prev => ({
                                            ...prev,
                                            template_name: selectedName,
                                            wid: selected?.wa_id || '',
                                            pid: selected?.p_id || ''
                                        }));
                                    }} required style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%', outline: 'none' }}>
                                        <option value="">Select Active Template</option>
                                        {templateOptions.map((tpl) => (
                                            <option key={tpl.id} value={tpl.name}>{tpl.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {visibleColumns.length > 0 && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Select Process Fields</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', maxHeight: '100px', overflowY: 'auto' }}>
                                        {visibleColumns.map(colName => (
                                            <label key={colName} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', background: '#fff', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                                <input type="checkbox" value={colName} checked={formData.selectedColumns?.includes(colName) || false} onChange={handleColumnCheckbox} />
                                                <span>{colName}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Is Header Required? (no-1, yes-0) */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Is Header Required?</label>
                                <div style={{ display: 'flex', gap: '16px', height: '38px', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input type="radio" name="is_header_required" value={0} checked={formData.is_header_required === 0} onChange={handleInputChange} /> Yes
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input type="radio" name="is_header_required" value={1} checked={formData.is_header_required === 1} onChange={handleInputChange} /> No
                                    </label>
                                </div>
                            </div>

                            {/* Header Configuration Fields (Shown only if Is Header Required is Yes / 0) */}
                            {formData.is_header_required === 0 && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Header Element Type</label>
                                            <select name="header_type" value={formData.header_type} onChange={handleInputChange}
                                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%', outline: 'none' }}>
                                                <option value="">Select Type</option>
                                                <option value="image">Image Asset</option>
                                                <option value="pdf">PDF Document</option>
                                                <option value="video">Video Loop</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Routing Context</label>
                                            <div style={{ display: 'flex', gap: '16px', height: '38px', alignItems: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" name="is_header_static" value={0} checked={formData.is_header_static === 0} onChange={handleInputChange} /> Static File
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" name="is_header_static" value={1} checked={formData.is_header_static === 1} onChange={handleInputChange} /> Dynamic DB Column
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Is Header ID? (no-0, yes-1)</label>
                                            <div style={{ display: 'flex', gap: '16px', height: '38px', alignItems: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" name="is_header_id" value={1} checked={formData.is_header_id === 1} onChange={handleInputChange} /> Yes
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input type="radio" name="is_header_id" value={0} checked={formData.is_header_id === 0} onChange={handleInputChange} /> No
                                                </label>
                                            </div>
                                        </div>

                                        <div>
                                            {formData.is_header_id === 1 ? (
                                                <div>
                                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Numerical Header ID Field</label>
                                                    <input type="number" name="header_id_val" value={formData.header_id_val || ''} onChange={handleInputChange} placeholder="Enter numeric header template ID" required
                                                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%', outline: 'none' }} />
                                                </div>
                                            ) : (
                                                formData.is_header_static === 0 ? (
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Upload Header Document</label>
                                                        <input type="file" disabled={!formData.header_type} accept={
                                                            formData.header_type === 'image' ? 'image/jpeg,image/png' :
                                                                formData.header_type === 'pdf' ? '.pdf' : 'video/mp4,video/mov,video/avi'
                                                        } onChange={handleFileChange} style={{ display: 'block', marginTop: '6px', fontSize: '12px' }} />
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Dynamic Target Header Column</label>
                                                        <input type="text" name="hcolumn" value={formData.hcolumn || ''} onChange={handleInputChange} placeholder="e.g. user_invoice_url"
                                                            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%', outline: 'none' }} />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#475569' }}>Language Locale</label>
                                    <select name="lang" value={formData.lang} onChange={handleInputChange}
                                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%', outline: 'none' }}>
                                        <option value="">Select Language</option>
                                        {languagePosts.map((post) => (
                                            <option key={post.id || post.language} value={post.language}>{post.language}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                                <button type="button" onClick={handleCancelEdit} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>Dismiss</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {["ID", "Template Module", "SQL Table Target", "Header Asset", "Mode", "WID Context", "UID", "Language", "Modify"].map((heading) => (
                                    <th key={heading} style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{heading}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody style={{ color: '#334155' }}>
                            {currentItems.length > 0 ? (
                                currentItems.map((config) => (
                                    <tr key={config.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '14px 16px', fontWeight: '500' }}>{config.id}</td>
                                        <td style={{ padding: '14px 16px', color: '#0f172a', fontWeight: '500' }}>{config.template_name}</td>
                                        <td style={{ padding: '14px 16px' }}><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{config.table_name}</code></td>
                                        <td style={{ padding: '14px 16px' }}>
                                            {config.header_image ? (
                                                (() => {
                                                    const file = config.header_image;
                                                    const ext = file.split('.').pop().toLowerCase();
                                                    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
                                                        return <img src={file} alt="Preview" style={{ height: '28px', borderRadius: '4px', objectFit: 'cover' }} />;
                                                    } else if (ext === 'pdf') {
                                                        return <a href={file} target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>PDF Doc</a>;
                                                    } else if (['mp4', 'webm'].includes(ext)) {
                                                        return <span style={{ color: '#64748b' }}>Video Media</span>;
                                                    }
                                                    return <span style={{ color: '#94a3b8' }}>Custom File</span>;
                                                })()
                                            ) : (
                                                <span style={{ color: '#94a3b8' }}>{config.hcolumn ? `Col: ${config.hcolumn}` : 'None'}</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{
                                                padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '500',
                                                background: config.is_header_static === 1 ? '#eff6ff' : '#f0fdf4',
                                                color: config.is_header_static === 1 ? '#1e40af' : '#166534'
                                            }}>
                                                {config.is_header_static === 1 ? 'Dynamic' : 'Static'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 16px', color: '#64748b' }}>{config.wa_id || 'N/A'}</td>
                                        <td style={{ padding: '14px 16px', color: '#64748b' }}>{config.user_id}</td>
                                        <td style={{ padding: '14px 16px' }}><span style={{ textTransform: 'uppercase', fontSize: '11px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{config.lang || 'en'}</span></td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <button onClick={() => handleEdit(config)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#2563eb', padding: '4px', borderRadius: '4px' }}>
                                                <FaEdit size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>No configurations deployed. Select your target profile options.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '14px 16px', background: '#fff', borderTop: '1px solid #e2e8f0'
                    }}>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>
                            Showing page <span style={{ fontWeight: '600', color: '#334155' }}>{currentPage}</span> of <span style={{ fontWeight: '600', color: '#334155' }}>{totalPages}</span> ({configs.length} records)
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '6px 12px', background: '#fff', border: '1px solid #cbd5e1',
                                    borderRadius: '6px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    color: currentPage === 1 ? '#cbd5e1' : '#334155', fontSize: '12px', fontWeight: '500'
                                }}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '6px 12px', background: '#fff', border: '1px solid #cbd5e1',
                                    borderRadius: '6px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    color: currentPage === totalPages ? '#cbd5e1' : '#334155', fontSize: '12px', fontWeight: '500'
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}